import { createContext, useContext, useEffect, useState } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

const LOCAL_USER_KEY = 'lyrapos_user';
const LOCAL_ACCESS_KEY = 'access_token';
const LOCAL_REFRESH_KEY = 'refresh_token';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem(LOCAL_USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (err) {
      return null;
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem(LOCAL_ACCESS_KEY));
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem(LOCAL_REFRESH_KEY));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const syncStorage = (authUser, access, refresh) => {
    if (authUser && access && refresh) {
      localStorage.setItem(LOCAL_USER_KEY, JSON.stringify(authUser));
      localStorage.setItem(LOCAL_ACCESS_KEY, access);
      localStorage.setItem(LOCAL_REFRESH_KEY, refresh);
      setUser(authUser);
      setToken(access);
      setRefreshToken(refresh);
      return;
    }

    localStorage.removeItem(LOCAL_USER_KEY);
    localStorage.removeItem(LOCAL_ACCESS_KEY);
    localStorage.removeItem(LOCAL_REFRESH_KEY);
    setUser(null);
    setToken(null);
    setRefreshToken(null);
  };

  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.login(email, password);
      const payload = response.data;
      if (!payload.success) {
        throw new Error(payload.message || 'Login failed');
      }
      const access = payload.data.access;
      const refresh = payload.data.refresh;
      const authUser = payload.data.user;
      syncStorage(authUser, access, refresh);
      
      // Clear selected branch to default to 'All Outlets' after login
      localStorage.removeItem('selectedBranch');
      
      // Dispatch event to clear branch from appStore
      window.dispatchEvent(new CustomEvent('auth:login', { detail: { user: authUser } }));
      
      return authUser;
    } catch (err) {
      setError(err.response?.data?.message ?? err.message ?? 'Unable to login');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (registerData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.register(registerData);
      const payload = response.data;
      if (!payload.success) {
        throw new Error(payload.message || 'Registration failed');
      }
      const access = payload.data.access;
      const refresh = payload.data.refresh;
      const authUser = payload.data.user;
      syncStorage(authUser, access, refresh);
      
      // Clear selected branch to default to 'All Outlets' after registration
      localStorage.removeItem('selectedBranch');
      
      // Dispatch event to clear branch from appStore
      window.dispatchEvent(new CustomEvent('auth:login', { detail: { user: authUser } }));
      
      return authUser;
    } catch (err) {
      setError(err.response?.data?.message ?? err.message ?? 'Unable to register');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = async (token) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.googleSignIn(token);
      const payload = response.data;
      if (!payload.success) {
        throw new Error(payload.message || 'Google sign-in failed');
      }
      const access = payload.data.access;
      const refresh = payload.data.refresh;
      const authUser = payload.data.user;
      syncStorage(authUser, access, refresh);
      
      // Clear selected branch to default to 'All Outlets' after Google sign-in
      localStorage.removeItem('selectedBranch');
      
      // Dispatch event to clear branch from appStore
      window.dispatchEvent(new CustomEvent('auth:login', { detail: { user: authUser } }));
      
      return authUser;
    } catch (err) {
      setError(err.response?.data?.message ?? err.message ?? 'Google sign-in failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    syncStorage(null, null, null);
    setError(null);
    window.location.href = '/login';
  };

  const clearError = () => setError(null);

  useEffect(() => {
    if (token && !user) {
      // Optional: you can call a getCurrentUser endpoint here
      setLoading(true);
      authService
        .getCurrentUser()
        .then((res) => {
          if (res.data.success) {
            setUser(res.data.data);
          }
        })
        .catch(() => {
          syncStorage(null, null, null);
        })
        .finally(() => setLoading(false));
    }
  }, [token, user]);

  const contextValue = {
    user,
    token,
    refreshToken,
    setUser,
    setToken,
    login,
    logout,
    register,
    googleSignIn,
    loading,
    error,
    clearError,
    isAuthenticated: Boolean(user && token),
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};
